import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import * as moment from 'moment';
import { VALIDATOR_ONLY_SPACES } from '../../../shared/constants/helpers.const';
import { customEmailValidator } from '../../../shared/validators/custom-validators.validator';
import { parsePhoneNumberFromString } from "libphonenumber-js/max";

@Injectable({ providedIn: 'root' })
export class UserDetailsFormProvider {
  userDetailsForm: FormGroup;
  addNewUserForm: FormGroup;
  constructor(private fb: FormBuilder) {}

  initForm(data?: any): FormGroup {
    return (this.userDetailsForm = this.fb.group(
      {
        firstName: [
          data?.firstName,
          [Validators.required, VALIDATOR_ONLY_SPACES],
        ],
        lastName: [
          data?.lastName,
          [Validators.required, VALIDATOR_ONLY_SPACES],
        ],
        email: [
          data?.email,
          [Validators.required, customEmailValidator(), VALIDATOR_ONLY_SPACES],
        ],
        username: [data?.username, [VALIDATOR_ONLY_SPACES]],
        organisationName: [data?.organisationName, [VALIDATOR_ONLY_SPACES]],
        addressLineOne: [data?.addressLineOne, [VALIDATOR_ONLY_SPACES]],
        addressLineTwo: [data?.addressLineTwo, [VALIDATOR_ONLY_SPACES]],
        dateOfBirth: [data?.dateOfBirth, [VALIDATOR_ONLY_SPACES]],
        city: [data?.city, [Validators.required, VALIDATOR_ONLY_SPACES]],
        state: [data?.state, [VALIDATOR_ONLY_SPACES]],
        area: [data?.area, [VALIDATOR_ONLY_SPACES]],
        countryCode: [data?.countryCode, [VALIDATOR_ONLY_SPACES]],
        zipCode: [data?.zipCode, [Validators.required, VALIDATOR_ONLY_SPACES]],
        country: [
          data?.country || '',
          [Validators.required, VALIDATOR_ONLY_SPACES],
        ],
        jobTitle: [data?.jobTitle, [VALIDATOR_ONLY_SPACES]],
        phoneCountryCode: ['', [VALIDATOR_ONLY_SPACES]],
        phoneAreaCode: ['', [VALIDATOR_ONLY_SPACES]],
        phoneNumber: ['', [VALIDATOR_ONLY_SPACES]],
      },
      {
        validators: [
          createPhoneValidator('phoneFieldsIncomplete', [
            'phoneCountryCode',
            'phoneAreaCode',
            'phoneNumber',
          ]),
        ],
      },
    ));
  }

  initFormAddNewUser(data?: any): FormGroup {
    const phoneNumberData: string[] = parsePhoneNumber(data?.phoneNumber || '');
    return (this.addNewUserForm = this.fb.group({
      firstName: [data?.firstName, [Validators.required]],
      lastName: [data?.lastName, [Validators.required]],
      email: [data?.email, [Validators.required, Validators.email]],
      password: ['', data?.id
        ? [Validators.minLength(6)]
        : [Validators.required, Validators.minLength(6)]],
      phone: this.fb.group(
        {
          phoneCountryCode: [phoneNumberData?.length ? phoneNumberData[0] : '', [VALIDATOR_ONLY_SPACES]],
          phoneAreaCode: [phoneNumberData?.length ? phoneNumberData[1] : '', [VALIDATOR_ONLY_SPACES]],
          phoneNumber: [phoneNumberData?.length ? phoneNumberData[2] : '', [VALIDATOR_ONLY_SPACES]],
        },
        {
          validators: [
            createPhoneValidator('phoneFieldsIncomplete', [
              'phoneCountryCode',
              'phoneAreaCode',
              'phoneNumber',
            ]),
          ],
        },
      ),
      username: [data?.username],
    }));
  }

  private parseDate(param: string) {
    return {
      year: parseInt(moment(param).format('YYYY'), 10),
      day: parseInt(moment(param).format('DD'), 10),
      month: parseInt(moment(param).format('M'), 10),
    };
  }
}

export const createPhoneValidator = (
  errorKey: string,
  fieldNames: string[],
): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const values = fieldNames.map((field) => control.get(field)?.value);
    const anyFilled = values.some((value) => !!value);
    const allFilled = values.every((value) => !!value);
    return !anyFilled || allFilled ? null : { [errorKey]: true };
  };
};

export function parsePhoneNumber(number: string): string[] {
  const isValidNumber = parsePhoneNumberFromString(number, 'IE')?.isValid();
  /*if (!isValidNumber && number.startsWith('00')) {
    number = number.replace(/^00/, '+');
  }*/
  if (!isValidNumber && !number.startsWith('+')) {
    number = number.startsWith('353') ? `+${number}` : `+353${number}`;
  }
  number = (number.startsWith('+') || number.startsWith('00')) ? number : `+${number}`;
  const phoneNumberParts = parsePhoneNumberFromString(number, 'IE')?.formatInternational().split(' ');
  const containsZeroAfterPrefix = /^\+3530/.test(number);
  /*if (containsZeroAfterPrefix) {
    phoneNumberParts?.splice(1,0, '0')
  }*/
  if (phoneNumberParts && phoneNumberParts.length >= 3) {
    return [
      phoneNumberParts[0], // Country code
      phoneNumberParts[1], // Area code
      // `${phoneNumberParts[2]}-${phoneNumberParts[3]}`, // Rest of the number
      phoneNumberParts.slice(2).join('-'),
    ];
  }
  return phoneNumberParts!;
}
